import { createFileRoute, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { buildProviderSlug } from "@/lib/provider-slug";
import {
 getProviderDirectoryPage,
 getProviderTotalCount,
 PROVIDERS_PER_DIRECTORY_PAGE,
} from "@/lib/seo.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/doctors/directory/$page")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const page = Number(params.page);
 if (!Number.isInteger(page) || page < 0) throw notFound();
 const [{ total }, { providers }] = await Promise.all([
 getProviderTotalCount(),
 getProviderDirectoryPage({ data: { page } }),
 ]);
 if (providers.length === 0) throw notFound();
 return { page, total, providers };
 },
 head: ({ loaderData }) => {
 const page = loaderData?.page ?? 0;
 const total = loaderData?.total ?? 0;
 const providers = loaderData?.providers ?? [];
 const totalPages = total > 0 ? Math.ceil(total / PROVIDERS_PER_DIRECTORY_PAGE) : 0;

 const url = `${SITE}/doctors/directory/${page}`;
 const title =
 page === 0
 ? "Healthcare Provider Directory | Heallexa"
 : `Healthcare Provider Directory \u2014 Page ${page} | Heallexa`;
 const description =
 total > 0
 ? `Browse ${total.toLocaleString()} healthcare provider profiles on Heallexa \u2014 page ${page + 1} of ${totalPages}.`
 : `Browse healthcare provider profiles on Heallexa, page ${page}.`;

 const breadcrumbs = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 {
 "@type": "ListItem",
 position: 2,
 name: "Healthcare Provider Directory",
 item: `${SITE}/doctors/directory`,
 },
 ...(page > 0
 ? [{ "@type": "ListItem", position: 3, name: `Page ${page}`, item: url }]
 : []),
 ],
 };

 const itemList =
 providers.length > 0
 ? {
 "@context": "https://schema.org",
 "@type": "ItemList",
 name: title,
 numberOfItems: providers.length,
 itemListElement: (providers as any[]).map((p, i) => ({
 "@type": "ListItem",
 position: page * PROVIDERS_PER_DIRECTORY_PAGE + i + 1,
 name: formatProviderLabel(p),
 url: `${SITE}/providers/${buildProviderSlug(p)}`,
 })),
 }
 : null;

 const links: Array<{ rel: string; href: string }> = [
 { rel: "canonical", href: url },
 ];
 if (page > 0)
 links.push({ rel: "prev", href: `${SITE}/doctors/directory/${page - 1}` });
 if (totalPages > 0 && page < totalPages - 1)
 links.push({ rel: "next", href: `${SITE}/doctors/directory/${page + 1}` });

 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ],
 links,
 scripts: [
 { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
 ...(itemList
 ? [{ type: "application/ld+json", children: JSON.stringify(itemList) }]
 : []),
 ],
 };
 },
 errorComponent: DirectoryError,
 notFoundComponent: DirectoryNotFound,
 component: DoctorsDirectoryPage,
});

function DirectoryError({ error, reset }: { error: Error; reset: () => void }) {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-2xl px-6 py-24 text-center">
 <h1 className="text-2xl font-semibold tracking-tight">This directory page is temporarily unavailable</h1>
 <p className="mt-3 text-sm text-muted-foreground">{error?.message ?? "Please try again in a moment."}</p>
 <button onClick={() => reset()} className="mt-6 px-4 py-2 bg-primary text-primary-foreground text-sm">Try again</button>
 <p className="mt-6 text-sm"><a href="/doctors/directory" className="text-primary hover:underline">← All directory pages</a></p>
 </main>
 <FooterSection />
 </div>
 );
}

function DirectoryNotFound() {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-2xl px-6 py-24 text-center">
 <h1 className="text-2xl font-semibold tracking-tight">Directory page not found</h1>
 <p className="mt-3 text-sm text-muted-foreground">This directory page doesn't exist.</p>
 <p className="mt-6 text-sm"><a href="/doctors/directory" className="text-primary hover:underline">← All directory pages</a></p>
 </main>
 <FooterSection />
 </div>
 );
}

function DoctorsDirectoryPage() {
 const { page, total, providers } = Route.useLoaderData();
 const totalPages = Math.ceil(total / PROVIDERS_PER_DIRECTORY_PAGE);
 const last = totalPages - 1;

 // Build a small window of page numbers around the current one.
 const windowSize = 7;
 const start = Math.max(0, page - Math.floor(windowSize / 2));
 const end = Math.min(last, start + windowSize - 1);
 const windowPages: number[] = [];
 for (let i = start; i <= end; i++) windowPages.push(i);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-6xl px-6 py-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-foreground">Sitemap</a>
 <span className="mx-2">/</span>
 <a href="/specialties" className="hover:text-foreground">Specialties</a>
 <span className="mx-2">/</span>
 <a href="/locations" className="hover:text-foreground">Locations</a>
 <span className="mx-2">/</span>
 <a href="/insurance" className="hover:text-foreground">Insurance</a>
 <span className="mx-2">/</span>
 <a href="/doctors/directory" className="hover:text-foreground">Doctors</a>
 <span className="mx-2">/</span>
 <span className="text-foreground">Page {page}</span>
 </nav>

 <header className="mb-8">
 <h1 className="text-4xl font-semibold tracking-tight text-foreground">
 Healthcare Provider Directory
 </h1>
 <p className="mt-3 text-muted-foreground">
 Page {page.toLocaleString()} of {totalPages.toLocaleString()}. Browse healthcare
 provider profiles on Heallexa.
 </p>
 </header>

 <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mb-12">
 {providers.map((p: any) => {
 const slug = buildProviderSlug(p);
 const label = formatProviderLabel(p);
 return (
 <li key={p.id} className="text-sm">
 <a
 href={`/providers/${slug}`}
 className="text-foreground hover:text-primary hover:underline"
 >
 {label}
 </a>
 {p.city && p.state ? (
 <span className="text-muted-foreground">, {p.city}, {p.state}</span>
 ) : null}
 </li>
 );
 })}
 </ul>

 <Pagination page={page} last={last} windowPages={windowPages} />

 <div className="mt-12 pt-8 border-t border-border text-sm text-muted-foreground space-x-4">
 <a href="/sitemap" className="hover:text-foreground">← Sitemap</a>
 <a href="/doctors/directory" className="hover:text-foreground">All directory pages</a>
 </div>
 </main>
 <FooterSection />
 </div>
 );
}

function formatProviderLabel(p: {
 name: string | null;
 credentials?: string | null;
 practice?: string | null;
 specialty?: string | null;
}) {
 const name = (p.name || "").trim();
 const creds = (p.credentials || "").trim();
 if (name && creds) return `${name}, ${creds}`;
 if (name) return name;
 if (p.practice) return p.practice;
 return p.specialty || "Healthcare provider";
}

function Pagination({
 page,
 last,
 windowPages,
}: {
 page: number;
 last: number;
 windowPages: number[];
}) {
 return (
 <nav className="flex flex-wrap items-center gap-2" aria-label="Directory pagination">
 <PageLink href="/doctors/directory/0" disabled={page === 0} label="First" />
 <PageLink
 href={`/doctors/directory/${Math.max(0, page - 1)}`}
 disabled={page === 0}
 label="Previous"
 />
 {windowPages.map((n) => (
 <a
 key={n}
 href={`/doctors/directory/${n}`}
 aria-current={n === page ? "page" : undefined}
 className={
 n === page
 ? "px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
 : "px-3 py-1.5 rounded-md bg-card border border-border text-sm hover:bg-accent transition"
 }
 >
 {n}
 </a>
 ))}
 <PageLink
 href={`/doctors/directory/${Math.min(last, page + 1)}`}
 disabled={page === last}
 label="Next"
 />
 <PageLink href={`/doctors/directory/${last}`} disabled={page === last} label={`Last ${last}`} />
 </nav>
 );
}

function PageLink({ href, disabled, label }: { href: string; disabled: boolean; label: string }) {
 if (disabled) {
 return (
 <span className="px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground/50 cursor-not-allowed">
 {label}
 </span>
 );
 }
 return (
 <a
 href={href}
 className="px-3 py-1.5 rounded-md bg-card border border-border text-sm hover:bg-accent transition"
 >
 {label}
 </a>
 );
}
